import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingCartSimple, X, PawPrint } from "@phosphor-icons/react";
import { imgUrl, formatErr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { FoundingHat } from "@/components/FoundingHat";
import MakeOfferModal from "@/components/MakeOfferModal";

const METHOD_LABELS = {
  paypal: "PayPal",
  cashapp: "Cash App",
  venmo: "Venmo",
  credit_card: "Credit Card",
  other: "Other",
};

/** Cart page — one card per seller with combined-shipping estimate,
 *  a per-seller payment-method picker (buyer must pick one method that
 *  every seller in their cart accepts), terms checkbox, and one Checkout
 *  button that opens a PO per seller. */
export default function Cart() {
  const { groups, itemCount, remove, checkout } = useCart();
  const [method, setMethod] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [offerGroup, setOfferGroup] = useState(null); // which group's Make-Offer modal is open
  const navigate = useNavigate();

  // Methods accepted by *every* seller in the cart.
  const availableMethods = groups.length === 0
    ? []
    : ["paypal", "cashapp", "venmo", "credit_card", "other"].filter((m) => groups.every((g) => g.accepts?.[m]));

  const submit = async () => {
    if (!method) { toast.error("Choose a payment method."); return; }
    if (!acceptTerms) { toast.error("Accept the Hunt57 Purchase Terms first."); return; }
    setBusy(true);
    try {
      const created = await checkout(method);
      toast.success(`${created.length} Purchase Order${created.length === 1 ? "" : "s"} opened.`);
      navigate("/transactions");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  if (itemCount === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="cart-empty">
        <ShoppingCartSimple size={48} weight="thin" className="mx-auto text-zinc-600" />
        <h1 className="mt-4 font-display font-black uppercase text-3xl tracking-tighter">Your cart is empty</h1>
        <p className="mt-2 text-zinc-500">Add cards from the browse board to open Purchase Orders with multiple sellers at once.</p>
        <Link to="/browse" className="h57-btn-primary inline-block mt-6" data-testid="cart-browse-link">Browse Cards →</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-10">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">→ Your Cart</div>
      <h1 className="mt-1 font-display font-black uppercase text-3xl md:text-4xl tracking-tighter">Checkout</h1>
      <p className="mt-2 text-sm text-zinc-400">
        Each seller becomes its own Purchase Order. Combined shipping is estimated below — the seller sets the final total when they validate.
      </p>

      <div className="mt-6 space-y-6">
        {groups.map((g) => (
          <div key={g.seller_id} className="h57-surface p-4" data-testid={`cart-group-${g.seller_username}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Seller</div>
                <Link to={`/u/${g.seller_username}`} className="font-display font-black uppercase text-lg hover:text-[#FF5F1F] inline-flex items-center gap-1.5">
                  @{g.seller_username}
                  <FoundingHat founding={g.seller_founding_member} size={14} />
                </Link>
              </div>
              <div className="text-right">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Subtotal + Est. Ship</div>
                <div className="font-display font-black text-xl" data-testid={`cart-subtotal-${g.seller_username}`}>
                  ${(g.subtotal + (g.combined_shipping?.estimated || 0)).toFixed(2)}
                </div>
                <div className="text-[10px] font-mono text-zinc-500">
                  ${g.subtotal.toFixed(2)} + ${(g.combined_shipping?.estimated || 0).toFixed(2)} ship
                  {g.combined_shipping?.enabled && " (combined)"}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (!method) { toast.error("Pick a payment method below first."); return; }
                    if (!g.accepts?.[method]) {
                      toast.error(`@${g.seller_username} doesn't accept ${method.replace("_"," ")}.`);
                      return;
                    }
                    setOfferGroup(g);
                  }}
                  disabled={busy}
                  data-testid={`cart-make-offer-${g.seller_username}`}
                  className="h57-btn-secondary text-xs !py-1.5 !px-3 mt-2"
                >
                  Make an Offer
                </button>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {g.listings.map((l) => (
                <div key={l.id} className="flex items-center gap-3 border border-white/5 p-2" data-testid={`cart-item-${l.id}`}>
                  <div className="w-16 h-16 bg-black border border-white/10 flex-shrink-0 flex items-center justify-center">
                    {l.front_image ? (
                      <img src={imgUrl(l.front_image, { w: 96 })} alt={l.title} className="w-full h-full object-contain" loading="lazy" />
                    ) : <PawPrint size={20} className="text-zinc-700" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/listing/${l.id}`} className="text-sm text-white line-clamp-2 hover:text-[#FF5F1F]">{l.title}</Link>
                    <div className="flex items-center gap-2 mt-0.5">
                      <div className="font-mono text-xs text-zinc-500">${l.price.toFixed(2)}</div>
                      {!l.accepts_offers && (
                        <span
                          className="border border-red-500 text-red-400 px-1 py-0.5 uppercase tracking-widest text-[9px] font-bold leading-none"
                          title="Firm price — cannot be included in an offer"
                          data-testid={`cart-firm-tag-${l.id}`}
                        >
                          Firm
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => remove(l.id)}
                    data-testid={`cart-remove-${l.id}`}
                    className="text-zinc-500 hover:text-red-400 p-1"
                    aria-label="Remove from cart"
                  >
                    <X size={16} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Payment method — must be accepted by ALL sellers in the cart. */}
      <div className="mt-8 h57-surface p-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">→ Payment Method</div>
        <p className="mt-1 text-xs text-zinc-500">
          Choose the method you&apos;ll use for ALL sellers. Only methods every seller in your cart accepts appear below.
        </p>
        {availableMethods.length === 0 ? (
          <div className="mt-3 p-3 border border-red-500/40 bg-red-500/5 text-sm text-red-300">
            No single payment method is accepted by every seller in your cart. Remove some items or contact the sellers to align.
          </div>
        ) : (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="cart-method-picker">
            {availableMethods.map((m) => (
              <button
                key={m}
                onClick={() => setMethod(m)}
                data-testid={`cart-method-${m}`}
                className={`p-3 border transition-colors ${method === m
                  ? "border-[#FF5F1F] bg-[#FF5F1F]/10 text-white"
                  : "border-white/15 hover:border-white/30 text-zinc-300"}`}
              >
                <div className="font-display font-bold text-sm">{METHOD_LABELS[m]}</div>
              </button>
            ))}
          </div>
        )}

        <label className="mt-5 flex items-start gap-2 cursor-pointer" data-testid="cart-accept-terms-label">
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            className="mt-1"
            data-testid="cart-accept-terms"
          />
          <span className="text-xs text-zinc-400">
            I understand Hunt57 does not process payments and is not a party to any sale. I&apos;ll pay each seller directly via the method above.
            I&apos;ve read the <Link to="/terms" className="underline hover:text-white">Terms</Link>.
          </span>
        </label>

        <div className="mt-5 flex flex-wrap gap-2 items-center justify-between">
          <Link to="/browse" data-testid="cart-continue-shopping" className="font-mono text-xs uppercase tracking-widest text-zinc-500 hover:text-white">
            ← Continue Browsing
          </Link>
          <button
            onClick={submit}
            disabled={busy || !method || !acceptTerms || availableMethods.length === 0}
            data-testid="cart-checkout-btn"
            className="h57-btn-primary inline-flex items-center gap-2 disabled:opacity-40"
          >
            {busy ? "Opening POs…" : `Open ${groups.length} Purchase Order${groups.length === 1 ? "" : "s"} →`}
          </button>
        </div>
      </div>

      {/* Make-Offer modal — opens for a specific seller group */}
      <MakeOfferModal
        open={!!offerGroup}
        onOpenChange={(v) => !v && setOfferGroup(null)}
        group={offerGroup}
        paymentMethod={method}
        onSubmitted={() => { setOfferGroup(null); navigate("/offers"); }}
      />
    </div>
  );
}
